/**
 * "Something has come in" (docs/19 A3, part 3) — always on the page, for papers
 * that do not arrive in the order the card expects them: a notice from the
 * Inquiry Officer, a direction from above, his letter asking for time.
 *
 * Drop the paper; it goes on the file at once. **The app's guess of what it is**
 * — from its heading or its file name, or one small call when neither says — is
 * offered for confirming: *Looks like: the inquiry report — Confirm / Pick
 * another*. **The paper is read only once the officer has said what it is**
 * (option 1, Phase A): never on the guess, so never read twice. The guess is
 * never counted until the officer confirms it (rule 4), and the pick list is the
 * vocabulary's, never a free-text box.
 *
 * A paper already on the file whose kind nobody has said is listed here too,
 * wherever it came in, because an unconfirmed paper satisfies nothing on the
 * card and the officer needs to see why.
 */

import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import type { CaseDetail, PaperKindOption, PaperOnFileView } from '../../api/client'
import { ErrorEnvelope } from '../../components/bits'
import { uploadPaper, useRefreshCase } from './acting'

/** How long the page keeps asking for the app's guess after an upload. */
const GUESS_WAIT_MS = 90_000
const GUESS_POLL_MS = 4_000

export function SomethingCameIn({ data }: { data: CaseDetail }) {
  const refresh = useRefreshCase(data.id)
  const queryClient = useQueryClient()
  const picker = useRef<HTMLInputElement>(null)
  const [over, setOver] = useState(false)
  const [uploadedAt, setUploadedAt] = useState<number | null>(null)

  const upload = useMutation({
    mutationFn: (file: File) => uploadPaper(data.id, file, null),
    onSuccess: () => {
      setUploadedAt(Date.now())
      refresh()
    },
  })

  const unconfirmed = (data.papers_on_file ?? []).filter((paper) => !paper.kind)
  const awaitingGuess = unconfirmed.some((paper) => !paper.kind_detected)

  /* **A paper said to be something is filed, and the card says so.** The row
     was there until the officer said what the paper was, and then it was not —
     no word that it had been filed, or as what, so the act read as the paper
     having been lost (walkthrough 32.34). What was said on this visit stays on
     the card as one line each. The name of the kind is the server's, read off
     the case once it has the paper; the words the officer chose stand in for
     the moment before that. */
  const [filed, setFiled] = useState<{ id: string; said: string }[]>([])
  const filedLines = filed.map((entry) => {
    const paper = (data.papers_on_file ?? []).find((row) => row.id === entry.id)
    return {
      id: entry.id,
      filename: paper?.filename ?? null,
      as: paper?.kind_label ?? entry.said,
    }
  })

  /* **The guess is the reader's, and the reader runs behind the upload.** While
     a paper just added has no guess yet, the case is asked again every few
     seconds — for a minute and a half, not for ever: a paper the reader cannot
     guess is still one the officer can name from the list below. */
  useEffect(() => {
    if (!uploadedAt || !awaitingGuess) return
    if (Date.now() - uploadedAt > GUESS_WAIT_MS) return
    const timer = window.setTimeout(() => {
      void queryClient.invalidateQueries({ queryKey: ['case', data.id] })
    }, GUESS_POLL_MS)
    return () => window.clearTimeout(timer)
  }, [uploadedAt, awaitingGuess, data, queryClient])

  const take = (file: File | undefined | null) => {
    if (file) upload.mutate(file)
  }

  return (
    <section className="came-in" data-testid="something-came-in">
      <h3>Something has come in</h3>
      <div
        className={`drop-zone${over ? ' over' : ''}`}
        data-testid="drop-zone"
        onDragOver={(event) => {
          event.preventDefault()
          setOver(true)
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(event) => {
          event.preventDefault()
          setOver(false)
          take(event.dataTransfer.files?.[0])
        }}
      >
        <input
          ref={picker}
          type="file"
          aria-label="A paper that has come in"
          style={{ display: 'none' }}
          data-testid="came-in-file"
          onChange={(event) => {
            take(event.target.files?.[0])
            event.target.value = ''
          }}
        />
        <span className="small muted">
          Drop a paper here — a notice, a letter, an order — or{' '}
        </span>
        <button
          type="button"
          className="link"
          disabled={upload.isPending}
          onClick={() => picker.current?.click()}
        >
          {upload.isPending ? 'putting it on the file…' : 'choose it'}
        </button>
        <span className="small muted">. It goes on the file at once.</span>
      </div>
      <ErrorEnvelope error={upload.error} />
      {unconfirmed.length ? (
        <ul className="came-in-list" aria-label="Papers waiting for you to say what they are">
          {unconfirmed.map((paper) => (
            <UnconfirmedPaper
              key={paper.id}
              data={data}
              paper={paper}
              kinds={data.paper_kinds ?? []}
              onFiled={(said) =>
                setFiled((was) => [
                  ...was.filter((entry) => entry.id !== paper.id),
                  { id: paper.id, said },
                ])
              }
            />
          ))}
        </ul>
      ) : null}
      {filedLines.length ? (
        <ul className="came-in-filed" aria-label="Filed just now" role="status">
          {filedLines.map((line) => (
            <li key={line.id} className="small" data-testid={`filed-${line.id}`}>
              Filed{line.filename ? <span className="mono"> {line.filename}</span> : null} as{' '}
              <strong>{line.as}</strong>. It is with the papers on the file.
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  )
}

function UnconfirmedPaper({
  data,
  paper,
  kinds,
  onFiled,
}: {
  data: CaseDetail
  paper: PaperOnFileView
  kinds: PaperKindOption[]
  /** The officer said what it is, and the server took it: the words chosen. */
  onFiled: (said: string) => void
}) {
  const refresh = useRefreshCase(data.id)
  // The officer's own move to the list; the guess shows whenever it exists and
  // they have not asked for the list instead — including a guess that arrives
  // after the page first drew this row.
  const [chosePicking, setPicking] = useState(false)
  const picking = chosePicking || !paper.kind_detected
  const [picked, setPicked] = useState<string>('')

  const confirm = useMutation({
    mutationFn: async (kind: string) => {
      await api.setPaperKind(data.id, paper.id, { preflight: false, kind })
      // A kind that is an external document records it on the rail, through
      // the label's own path — the same as an upload naming the document.
      const records = kinds.find((option) => option.key === kind)?.records_document
      if (records && !paper.document_type) {
        await api.labelSourceFile(data.id, paper.id, {
          preflight: false,
          document_type: records,
        })
      }
    },
    onSuccess: (_done, kind) => {
      // The label the officer chose — the list's, or the guess's own — until
      // the case comes back carrying the server's name for it.
      onFiled(
        kinds.find((option) => option.key === kind)?.label ??
          (kind === paper.kind_detected ? paper.kind_detected_label : null) ??
          kind,
      )
      refresh()
    },
  })

  return (
    <li className="came-in-paper" data-testid={`came-in-${paper.id}`}>
      <a
        className="mono small"
        href={api.sourceFilePath(data.id, paper.id)}
        target="_blank"
        rel="noreferrer"
      >
        {paper.filename}
      </a>
      {paper.kind_detected && !picking ? (
        <span className="guess">
          <span>
            Looks like: <strong>{paper.kind_detected_label ?? paper.kind_detected}</strong>
          </span>
          {paper.kind_reason ? (
            <span className="small muted"> — {paper.kind_reason}</span>
          ) : null}
          <button
            type="button"
            className="primary"
            disabled={confirm.isPending}
            onClick={() => confirm.mutate(paper.kind_detected as string)}
            data-testid={`confirm-guess-${paper.id}`}
          >
            Confirm
          </button>
          <button type="button" className="quiet" onClick={() => setPicking(true)}>
            Pick another
          </button>
          <span className="small muted"> It is read once you say what it is.</span>
        </span>
      ) : (
        <span className="guess">
          {!paper.kind_detected ? (
            <span className="small muted">
              The app has no guess yet — what is it? It is read once you say.
            </span>
          ) : null}
          <select
            aria-label={`What ${paper.filename} is`}
            value={picked}
            onChange={(event) => setPicked(event.target.value)}
            data-testid={`pick-kind-${paper.id}`}
          >
            <option value="">— pick what it is —</option>
            {kinds.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="primary"
            disabled={!picked || confirm.isPending}
            onClick={() => confirm.mutate(picked)}
          >
            {confirm.isPending ? 'Recording…' : 'That is what it is'}
          </button>
          {paper.kind_detected ? (
            <button type="button" className="quiet" onClick={() => setPicking(false)}>
              Back to the app&apos;s guess
            </button>
          ) : null}
        </span>
      )}
      <ErrorEnvelope error={confirm.error} />
    </li>
  )
}
